import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { usePlanFeatures } from '../../../../contexts/PlanFeaturesContext';
import { collectFeedbackNow, loadFeedbackItems, loadFeedbackStats, loadHistoryStatus } from '../../../../services/feedback';
import FeedbackAnalytics from './FeedbackAnalytics';
import FeedbackBilling from './FeedbackBilling';
import FeedbackKnowledge from './FeedbackKnowledge';
import FeedbackSettings from './FeedbackSettings';
import SupportAuto from '../support/SupportAuto';
import { ItemCard } from './FeedbackItems';
import { errorText } from './feedbackUi';
import './feedback.css';

const PAGE = 50;
const KIND_NAMES = { review: 'отзывы', question: 'вопросы', chat: 'чаты' };
const STAGE_HELP = {
    waiting: 'Обращения, на которые ещё нет ни ответа, ни черновика. Начинайте отсюда.',
    draft: 'Ответ для обращения готов, но не отправлен: ИИ подготовил черновик, и он ждёт вашего подтверждения (или стоит режим «черновик»).',
    failed: 'Ответ пытались отправить, но агент не смог: не нашёл обращение в списке или страница Seller не открылась. Откройте обращение и нажмите «Отправить» ещё раз. Часть таких ошибок система повторяет сама, до трёх раз.',
    moderation: 'Ответ отправлен в Ozon и проходит проверку у них (или ещё стоит в очереди у агента). Делать ничего не нужно.',
    sent: 'Ответ опубликован на Ozon.',
    skipped: 'Отвечать не нужно или нельзя: отзыв только с оценкой без текста; на обращение уже ответил магазин на Ozon; заказ отменён (Ozon не даёт ответить); отзыв скрыт; вы пропустили обращение вручную; в чате появилось новое сообщение. Выберите плитку, чтобы увидеть причину у каждого обращения.',
    all: 'Все собранные отзывы, вопросы и чаты по текущим фильтрам, включая закрытые и отправленные.',
};

const VIEWS = [
    { id: 'items', label: 'Обращения' },
    { id: 'support', label: 'Техподдержка' },
    { id: 'knowledge', label: 'База знаний' },
    { id: 'analytics', label: 'Анализ' },
    { id: 'billing', label: 'Биллинг' },
    { id: 'settings', label: 'Настройки' },
];

// Причины маршрута для фильтра: сначала сгруппированные (претензии, цена или наличие), затем одиночные.
const REASON_FILTERS = [
    ['claim', 'Претензии и возвраты'],
    ['price_stock', 'Цена, акция или наличие'],
    ['already_answered', 'Уже есть ответ'],
    ['no_text', 'Только оценка, без текста'],
    ['rule_authenticity', 'Подлинность'],
    ['rule_delivery', 'Доставка'],
    ['rule_words', 'Ваши слова'],
    ['risk_stopword', 'Риск: юридическая или медицинская тема'],
    ['ai_risk', 'ИИ: риск'],
    ['ai_no_fact', 'ИИ: нет нужного факта'],
    ['ai_unclear', 'ИИ: непонятный запрос'],
    ['negative_review', 'Негативный отзыв'],
    ['short_praise', 'Короткая похвала'],
    ['needs_review', 'Положительный, ответ ИИ'],
    ['superseded', 'Есть новое сообщение в чате'],
    ['hidden', 'Скрыт, ответ не виден'],
    ['cancelled_order', 'Отменённый заказ'],
    ['manual', 'Пропущено вручную'],
];

function QueueView({ accountId, refreshKey, onChanged }) {
    const { request } = useApi();
    const [kind, setKind] = useState('');
    const [query, setQuery] = useState('');
    const [status, setStatus] = useState('all');
    const [hasText, setHasText] = useState('yes');
    const [rating, setRating] = useState('');
    const [reason, setReason] = useState('');
    const [offset, setOffset] = useState(0);
    const [stats, setStats] = useState(null);
    const [data, setData] = useState({ total: 0, items: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [history, setHistory] = useState(null);
    const [helpKey, setHelpKey] = useState('');

    // Ход сбора истории: обновляется, пока в очереди есть окна.
    useEffect(() => {
        let cancelled = false;
        let timer = null;
        const tick = async () => {
            try {
                const value = await loadHistoryStatus({ request, accountId });
                if (cancelled) return;
                setHistory(value);
                if (value.waiting > 0) timer = setTimeout(tick, 20000);
            } catch (err) {
                // не критично: без индикатора список работает
            }
        };
        tick();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [request, accountId, refreshKey]);

    const reload = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [list, summary] = await Promise.all([
                loadFeedbackItems({ request, accountId, status, kind, query: query.trim(), hasText, rating, reason, limit: PAGE, offset }),
                loadFeedbackStats({ request, accountId, hasText, kind }),
            ]);
            setData(list);
            setStats(summary);
        } catch (err) {
            setError(errorText(err));
        } finally {
            setLoading(false);
        }
    }, [request, accountId, status, kind, query, hasText, rating, reason, offset]);

    useEffect(() => { reload(); }, [reload, refreshKey]);

    // Любой фильтр возвращает список на первую страницу.
    const filter = (setter) => (value) => { setter(value); setOffset(0); };
    // Оценка и «с текстом / без» есть только у отзывов: у вопросов и чатов эти фильтры не работают, поэтому их сбрасываем.
    const changeKind = (value) => { setKind(value); setOffset(0); if (value !== 'review') { setRating(''); setHasText('yes'); } };

    const collect = async () => {
        setNotice('');
        try {
            const result = await collectFeedbackNow({ request, accountId });
            setNotice(result.scheduled?.length ? 'Задачи сбора поставлены агенту' : 'Сбор уже идёт или ещё не прошёл интервал');
        } catch (err) {
            setNotice(errorText(err));
        }
    };

    return (
        <div className="fb">
            {stats && (
                <div className="fb-stats">
                    {[
                        ['waiting', stats.stages?.waiting ?? stats.waiting, 'ждут ответа'],
                        ['draft', stats.stages?.draft ?? 0, 'черновики'],
                        ['failed', stats.stages?.failed ?? 0, 'не отправлено'],
                        ['moderation', stats.stages?.moderation ?? 0, 'на модерации Ozon'],
                        ['sent', stats.sent, 'отправлено'],
                        ['skipped', stats.skipped, 'закрыто правилами'],
                        ['all', stats.total, `всего собрано${kind ? ` (${KIND_NAMES[kind]})` : ''}`],
                    ].map(([key, count, label]) => (
                        <div key={key} className="fb-stat-wrap">
                            <button type="button" className={`fb-stat fb-stat--button${status === key ? ' is-active' : ''}`}
                                onClick={() => filter(setStatus)(key)}>
                                <b>{count}</b><span>{label}</span>
                            </button>
                            <span className="fb-help" role="button" tabIndex={0} title={STAGE_HELP[key]} aria-label={`Что значит «${label}»`}
                                onClick={() => setHelpKey(helpKey === key ? '' : key)}
                                onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setHelpKey(helpKey === key ? '' : key); }}>?</span>
                        </div>
                    ))}
                </div>
            )}
            {helpKey && <div className="fb-note fb-help-text">{STAGE_HELP[helpKey]}</div>}
            {stats && !kind && (
                <div className="fb-note">
                    Собрано: отзывов {stats.by_kind?.review || 0}, вопросов {stats.by_kind?.question || 0}, чатов {stats.by_kind?.chat || 0}
                    {hasText === 'yes' ? ' (только с текстом)' : ''}. Счётчики выше считают всё вместе.
                </div>
            )}
            {history && history.windows > 0 && (history.waiting > 0 || history.failed > 0) && (
                <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <b>{history.waiting > 0 ? 'Идёт сбор истории отзывов' : 'Сбор истории завершён не полностью'}</b>
                    <div className="fb-muted">
                        Периодов готово {history.done} из {history.windows}, в очереди {history.waiting}{history.failed ? `, не удалось ${history.failed}` : ''}.
                        {' '}Собрано отзывов: {history.rows}{history.covered_from ? `, покрыто с ${history.covered_from.split('-').reverse().join('.')}` : ''}
                        {history.next_window ? `, дальше с ${history.next_window.split('-').reverse().join('.')}` : ''}.
                        {history.running ? ' Агент работает над периодом сейчас.' : ' Агент возьмёт следующий период, когда освободится.'}
                    </div>
                </div>
            )}
            <div className="fb-row">
                <select className="fb-input" value={kind} onChange={(event) => changeKind(event.target.value)}>
                    <option value="">Отзывы, вопросы и чаты</option>
                    <option value="review">Только отзывы</option>
                    <option value="question">Только вопросы</option>
                    <option value="chat">Только чаты</option>
                </select>
                {kind === 'review' && (
                    <>
                    <select className="fb-input" value={rating} onChange={(event) => filter(setRating)(event.target.value)}>
                        <option value="">Любая оценка</option>
                        {[5, 4, 3, 2, 1].map((star) => <option key={star} value={String(star)}>{star}★</option>)}
                    </select>
                    <select className="fb-input" value={hasText} onChange={(event) => filter(setHasText)(event.target.value)}>
                        <option value="">С текстом и без</option>
                        <option value="yes">Только с текстом</option>
                        <option value="no">Только без текста (одна оценка)</option>
                    </select>
                    </>
                )}
                <select className="fb-input" value={reason} onChange={(event) => filter(setReason)(event.target.value)} title="Причина, по которой обращение так обработано">
                    <option value="">Любая причина</option>
                    {REASON_FILTERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <input className="fb-input" style={{ width: 240 }} placeholder="Поиск по тексту" value={query} onChange={(event) => filter(setQuery)(event.target.value)} />
                <button type="button" onClick={collect}>Собрать сейчас</button>
                {notice && <span className="fb-note">{notice}</span>}
            </div>
            {error && <div className="fb-card fb-chip--bad">{error}</div>}
            {loading && data.items.length === 0 && <div className="fb-empty">Загрузка…</div>}
            {!loading && !error && data.items.length === 0 && (
                <div className="fb-card fb-empty">
                    {status === 'waiting'
                        ? 'Очередь пуста. Новые отзывы и вопросы появятся здесь после очередного сбора. Отзывы без текста (только оценка) в очередь не попадают.'
                        : 'По этим фильтрам обращений нет. Измените фильтры или дождитесь сбора.'}
                </div>
            )}
            <div className="fb-list">
                {data.items.map((item) => <ItemCard key={item.id} item={item} onChanged={() => { reload(); onChanged(); }} />)}
            </div>
            {data.total > PAGE && (
                <div className="fb-row">
                    <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(offset - PAGE, 0))}>Назад</button>
                    <span className="fb-note">{offset + 1}–{Math.min(offset + PAGE, data.total)} из {data.total}</span>
                    <button type="button" disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>Дальше</button>
                </div>
            )}
        </div>
    );
}

function FeedbackPanel({ accountId }) {
    const { hasFeature } = usePlanFeatures();
    const [view, setView] = useState('items');
    const [refreshKey, setRefreshKey] = useState(0);
    const bump = useCallback(() => setRefreshKey((value) => value + 1), []);

    if (!accountId) return <p className="muted">Выберите кабинет.</p>;

    return (
        <div className="fb">
            <div className="account-tabs account-tabs--secondary">
                {VIEWS.filter((item) => item.id !== 'support' || hasFeature('support_auto')).map((item) => (
                    <button key={item.id} type="button" className={view === item.id ? 'active' : ''} onClick={() => setView(item.id)}>{item.label}</button>
                ))}
            </div>
            {view === 'items' && <QueueView accountId={accountId} refreshKey={refreshKey} onChanged={bump} />}
            {view === 'support' && <SupportAuto accountId={accountId} />}
            {view === 'knowledge' && <FeedbackKnowledge accountId={accountId} />}
            {view === 'analytics' && <FeedbackAnalytics accountId={accountId} />}
            {view === 'billing' && <FeedbackBilling accountId={accountId} />}
            {view === 'settings' && <FeedbackSettings accountId={accountId} onSaved={bump} onOpen={setView} />}
        </div>
    );
}

export default FeedbackPanel;
